import { Link } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import logo from "../assets/logo.jpeg";
import { Menu, X, ArrowUp } from "lucide-react";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdownDesktop, setOpenDropdownDesktop] = useState(false);
  const [openDropdownMobile, setOpenDropdownMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showFullHeader, setShowFullHeader] = useState(true);
  const dropdownRef = useRef(null);
  const closeDropdown = () => setOpenDropdownDesktop(false);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdownDesktop(false);
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    return () =>
      document.removeEventListener("pointerdown", handleClickOutside);
  }, []);

  // Scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    let lastScroll = 0;

    const handleScroll = () => {
      const current = window.scrollY;

      // Show scroll-top button early
      setIsVisible(current > 80);

      // Mini header
      if (current > lastScroll && current > 150) {
        setShowFullHeader(false);
      } else {
        setShowFullHeader(true);
      }

      lastScroll = current;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* HEADER */}
      <header
        className="fixed top-0 left-0 w-full z-50 bg-gradient-to-r 
        from-primary via-[#3b4da1] to-[#4b5cc2]
        shadow-lg border-b border-white/10 backdrop-blur-md
        transition-all duration-500"
      >
        <div className="w-full max-w-7xl mx-auto px-4">
          
          {/* ==================== DESKTOP HEADER ==================== */}
          <div className="hidden md:block">
            {/* ---------- FULL HEADER (TOP VIEW) ---------- */}
            <div
              className={`flex flex-col items-center text-center transition-all duration-500 ${
                showFullHeader
                  ? "opacity-100 max-h-[300px] py-4"
                  : "opacity-0 max-h-0 overflow-hidden py-0"
              }`}
            >
              {/* Logo + Name */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Logo Wrapper */}
                <div className="flex justify-center sm:justify-start w-full sm:w-auto">
                  <div className="w-16 sm:w-20 sm:h-20 rounded-full bg-accent shadow-md overflow-hidden flex items-center justify-center flex-shrink-0">
                    <img
                      src={logo}
                      alt="logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>

                {/* Name */}
                <h1 className="text-base sm:text-xl md:text-[30px] text-white font-semibold leading-tight">
                  JEEVA SHAKTHI HOUSING MULTI-STATE CO-OPERATIVE SOCIETY LTD
                </h1>
              </div>

              {/* Reg No. */}
              <p className="text-[13px] sm:text-[15px] md:text-[16px] text-[#F9B208] font-normal mt-2 md:mt-[-15px] sm:mt-1">
                Reg No. MSCS/CR/1407/2023,&nbsp;
                <span className="text-white/90">
                  Registered Under Multi-State Cooperative Societies Act-2002
                </span>
              </p>

              {/* Full Registration Info */}
              <p className="text-[13px] sm:text-[15px] md:text-[16px] text-white/90 mb-3 font-normal leading-snug">
                By Central Registrar of Cooperative Societies, Ministry of Cooperation,
                Government of India, New Delhi, India.
              </p>
            </div>

            {/* Divider (Only visible when full header is visible) */}
            <div className={`w-full h-[1px] bg-white/20 transition-all duration-500 ${showFullHeader ? 'block' : 'hidden'}`}></div>

            {/* ---------- MINI HEADER (SCROLL MODE) ---------- */}
            {!showFullHeader && (
              <div className="flex items-center justify-between py-3 text-white">
                {/* LEFT SIDE: Logo + Name */}
                <a href="/#home">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full overflow-hidden shadow-md">
                      <img src={logo} alt="logo" className="h-full w-full object-cover" />
                    </div>
                    <p className="text-sm font-semibold tracking-wide leading-tight">
                      JEEVA SHAKTHI HOUSING MULTI-STATE <br /> CO-OPERATIVE
                      SOCIETY LTD
                    </p>
                  </div>
                </a>

                {/* RIGHT SIDE: NAV LINKS */}
                <nav className="flex items-center space-x-8 text-sm font-semibold">
                  <a href="/#home" className="hover:text-accent">
                    Home
                  </a>
                  <a href="/#aboutus" className="hover:text-accent">
                    About Us
                  </a>

                  <div className="relative" ref={dropdownRef}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdownDesktop((prev) => !prev);
                      }}
                      className="hover:text-accent"
                    >
                      Documentation & Benefits
                    </button>

                    <div
                      className={`absolute left-1/2 -translate-x-1/2 mt-3 w-56 bg-white text-primary 
                      shadow-lg rounded-xl transition-all duration-300 overflow-hidden z-50 ${
                        openDropdownDesktop
                          ? "opacity-100 scale-100"
                          : "opacity-0 scale-95 pointer-events-none"
                      }`}
                    >
                      <a
                        href="/#governmentschemes"
                        className="block px-4 py-2 hover:bg-accent/10"
                        onClick={closeDropdown}
                      >
                        Schemes
                      </a>
                      <a
                        href="/#affordablehousingadvantage"
                        className="block px-4 py-2 hover:bg-accent/10"
                        onClick={closeDropdown}
                      >
                        Housing
                      </a>
                      <a
                        href="/#ministry"
                        className="block px-4 py-2 hover:bg-accent/10"
                        onClick={closeDropdown}
                      >
                        Ministry
                      </a>
                      <a
                        href="../gallery/Jeeva Sakthi Housing Multistate Industrial Cooperative Society Ltd By Laws.pdf"
                        target="_blank"
                        onClick={closeDropdown}
                        className="block px-4 py-2 hover:bg-accent/10"
                      >
                        By-Laws (PDF)
                      </a>
                      <Link
                        to="/membership"
                        onClick={closeDropdown}
                        className="block px-4 py-2 hover:bg-accent/10"
                      >
                        Membership Form
                      </Link>
                    </div>
                  </div>

                  <Link to="/registered-members" className="hover:text-accent">
                    Members
                  </Link>
                  <a href="/#ourgallery" className="hover:text-accent">
                    Gallery
                  </a>

                  <Link to="/membership">
                    <button className="bg-gradient-to-r from-accent to-[#f9a634] px-5 py-2 rounded-full shadow-md hover:scale-105 transition">
                      Join Now
                    </button>
                  </Link>
                </nav>
              </div>
            )}

            {/* ---------- FULL DESKTOP NAV (TOP VIEW) ---------- */}
            {showFullHeader && (
              <nav className="flex items-center justify-center space-x-10 py-3 text-white font-semibold">
                <a href="/#home" className="hover:text-accent">
                  Home
                </a>
                <a href="/#aboutus" className="hover:text-accent">
                  About Us
                </a>

                {/* Desktop Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenDropdownDesktop((prev) => !prev);
                    }}
                    className="hover:text-accent"
                  >
                    Documentation & Benefits
                  </button>

                  <div
                    className={`absolute left-1/2 -translate-x-1/2 mt-3 w-56 bg-white text-primary 
                    shadow-lg rounded-xl transition-all duration-300 overflow-hidden z-50 ${
                      openDropdownDesktop
                        ? "opacity-100 scale-100"
                        : "opacity-0 scale-95 pointer-events-none"
                    }`}
                  >
                    <a
                      href="/#governmentschemes"
                      className="block px-4 py-2 hover:bg-accent/10"
                      onClick={closeDropdown}
                    >
                      Schemes
                    </a>
                    <a
                      href="/#affordablehousingadvantage"
                      className="block px-4 py-2 hover:bg-accent/10"
                      onClick={closeDropdown}
                    >
                      Housing
                    </a>
                    <a
                      href="/#ministry"
                      className="block px-4 py-2 hover:bg-accent/10"
                      onClick={closeDropdown}
                    >
                      Ministry
                    </a>
                    <a
                      href="../gallery/Jeeva Sakthi Housing Multistate Industrial Cooperative Society Ltd By Laws.pdf"
                      target="_blank"
                      className="block px-4 py-2 hover:bg-accent/10"
                      onClick={closeDropdown}
                    >
                      By-Laws (PDF)
                    </a>
                    <Link
                      to="/membership"
                      onClick={closeDropdown}
                      className="block px-4 py-2 hover:bg-accent/10"
                    >
                      Membership Form
                    </Link>
                  </div>
                </div>

                <Link to="/registered-members" className="hover:text-accent">
                  Members
                </Link>
                <a href="/#ourgallery" className="hover:text-accent">
                  Gallery
                </a>

                <Link to="/membership">
                  <button className="bg-gradient-to-r from-accent to-[#f9a634] px-6 py-2 rounded-full shadow-md hover:scale-105 transition">
                    Join Now
                  </button>
                </Link>
              </nav>
            )}
          </div>

          {/* ==================== MOBILE HEADER ==================== */}
          <div className="flex md:hidden items-center justify-between py-3 text-white">
            {/* LEFT SIDE: Logo + Compact Name */}
            <a href="/#home" className="flex items-center gap-2 max-w-[80%]">
              <div className="h-10 w-10 rounded-full overflow-hidden shadow-md bg-accent flex-shrink-0 flex items-center justify-center">
                <img src={logo} alt="logo" className="h-full w-full object-contain" />
              </div>
              <div className="text-left leading-tight">
                <p className="text-xs font-bold tracking-wide uppercase">
                  Jeeva Shakthi Housing
                </p>
                <p className="text-[9px] text-[#F9B208] font-semibold tracking-tighter">
                  Multistate Co-operative Society Ltd
                </p>
              </div>
            </a>

            {/* RIGHT SIDE: Hamburger Button */}
            <button
              className="text-white focus:outline-none p-1"
              onClick={() => {
                setIsMenuOpen(!isMenuOpen);
                setOpenDropdownMobile(false);
              }}
            >
              {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>

          {/* ---------- MOBILE MENU DRAWER ---------- */}
          {isMenuOpen && (
            <div className="md:hidden bg-gradient-to-b from-[#323879] via-[#3b4da1] to-[#4b5cc2] text-white text-center pb-6 pt-2 space-y-3 shadow-lg border-t border-white/10 max-h-[80vh] overflow-y-auto animate-slideDown">
              <a
                href="/#home"
                onClick={() => setIsMenuOpen(false)}
                className="block text-base font-semibold py-2 hover:bg-white/10 hover:text-accent transition"
              >
                Home
              </a>

              <a
                href="/#aboutus"
                onClick={() => setIsMenuOpen(false)}
                className="block text-base font-semibold py-2 hover:bg-white/10 hover:text-accent transition"
              >
                About Us
              </a>

              {/* Mobile Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setOpenDropdownMobile(!openDropdownMobile)}
                  className="w-full px-4 py-2 font-semibold hover:bg-white/10 hover:text-accent flex justify-center items-center gap-2 transition"
                >
                  Documentation & Benefits
                  <span className="text-xs">{openDropdownMobile ? "▲" : "▼"}</span>
                </button>

                {openDropdownMobile && (
                  <div className="mt-1 mx-4 bg-white text-primary rounded-xl shadow-lg overflow-hidden border border-white/20 transition-all duration-300">
                    <a
                      href="/#governmentschemes"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setOpenDropdownMobile(false);
                      }}
                      className="block px-4 py-2.5 text-sm font-semibold hover:bg-accent/10 transition border-b border-gray-100"
                    >
                      Schemes
                    </a>

                    <a
                      href="/#affordablehousingadvantage"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setOpenDropdownMobile(false);
                      }}
                      className="block px-4 py-2.5 text-sm font-semibold hover:bg-accent/10 transition border-b border-gray-100"
                    >
                      Housing
                    </a>

                    <a
                      href="/#ministry"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setOpenDropdownMobile(false);
                      }}
                      className="block px-4 py-2.5 text-sm font-semibold hover:bg-accent/10 transition border-b border-gray-100"
                    >
                      Ministry
                    </a>

                    <a
                      href="../gallery/Jeeva Sakthi Housing Multistate Industrial Cooperative Society Ltd By Laws.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setOpenDropdownMobile(false);
                      }}
                      className="block px-4 py-2.5 text-sm font-semibold hover:bg-accent/10 transition border-b border-gray-100"
                    >
                      By-Laws (PDF)
                    </a>

                    <Link
                      to="/membership"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setOpenDropdownMobile(false);
                      }}
                      className="block px-4 py-2.5 text-sm font-semibold hover:bg-accent/10 transition"
                    >
                      Membership Form
                    </Link>
                  </div>
                )}
              </div>

              <Link
                to="/registered-members"
                onClick={() => setIsMenuOpen(false)}
                className="block text-base font-semibold py-2 hover:bg-white/10 hover:text-accent transition"
              >
                Members
              </Link>

              <a
                href="/#ourgallery"
                onClick={() => setIsMenuOpen(false)}
                className="block text-base font-semibold py-2 hover:bg-white/10 hover:text-accent transition"
              >
                Gallery
              </a>

              <div className="pt-2">
                <Link
                  to="/membership"
                  onClick={() => setIsMenuOpen(false)}
                  className="inline-block bg-gradient-to-r from-accent to-[#f9a634] px-8 py-2.5 rounded-full font-bold text-sm shadow-md hover:scale-105 transition-all duration-300"
                >
                  Join Now
                </Link>
              </div>

              {/* Mobile Info Footer inside Menu */}
              <div className="pt-4 px-4 text-[10px] text-white/60 leading-tight space-y-1">
                <p className="font-semibold text-white/80">Reg No. MSCS/CR/1407/2023</p>
                <p>Registered Under Multi-State Cooperative Societies Act-2002</p>
                <p>Ministry of Cooperation, Govt of India, New Delhi</p>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* GO TO TOP BUTTON */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 p-3 rounded-full bg-accent text-white shadow-xl z-50
        transition-all duration-300 ${isVisible ? "opacity-100" : "opacity-0"}`}
      >
        <ArrowUp size={24} />
      </button>
    </>
  );
};

export default Navbar;
